import React from 'react';
import { useTranslation } from 'react-i18next';
import { BdsIcon, BdsDivider } from '@/core/constants/webComponents';
import { CARD_POST_EVENTS, CardPostEventName } from '@/features/publication-wall/constants/events';
import './FooterMetaModal.scss';

interface FooterMetaModalProps {
  groups: Array<{ name: string; count?: number }>;
  themes: Array<{ name: string; count?: number }>;
  interest?: Array<{ name: string }>;
  postId?: string;
  eventName?: CardPostEventName;
  onClose: () => void;
}

export const FooterMetaModal: React.FC<FooterMetaModalProps> = ({
  groups,
  themes,
  interest,
  postId,
  eventName = CARD_POST_EVENTS.FOOTER_META_CLICK,
  onClose,
}) => {
  const { t } = useTranslation();

  const formatItem = (name: string, count?: number) => (count ? `${name} (+${count})` : name);

  const dispatchClick = (section: 'group' | 'theme' | 'interest', value: string, index?: number) => {
    document.dispatchEvent(
      new CustomEvent(eventName, {
        detail: { postId, section, value, index },
        bubbles: true,
        composed: true,
      }),
    );
  };

  return (
    <div
      className="footer-meta-modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      role="presentation"
    >
      <dialog
        open
        className="footer-meta-modal-container"
        aria-labelledby="footer-meta-modal-title"
      >
        <div
          role="none"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="footer-meta-modal-header">
            <h2 id="footer-meta-modal-title" className="footer-meta-modal-title">
              {t('cardPosts.allFooterMetaTitle')}
            </h2>
            <button
              type="button"
              className="footer-meta-modal-close"
              onClick={onClose}
              aria-label={t('cardPosts.close') ?? 'Close'}
            >
              <BdsIcon name="close" size="small" />
            </button>
          </div>
          <BdsDivider />
          <div className="footer-meta-modal-body">
            {groups.length > 0 && (
              <div className="footer-meta-modal-section">
                <span className="footer-meta-modal-label">{t('cardPosts.group').replaceAll(':', '')}:</span>
                <div className="footer-meta-modal-list">
                  {groups.map((item, index) => {
                    const text = formatItem(item.name, item.count);
                    return (
                      <button
                        key={`group-${item.name}-${index}`}
                        type="button"
                        className="footer-meta-modal-item"
                        onClick={() => {
                          dispatchClick('group', text, index);
                          onClose();
                        }}
                      >
                        {text}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {themes.length > 0 && (
              <div className="footer-meta-modal-section">
                <span className="footer-meta-modal-label">{t('cardPosts.theme').replaceAll(':', '')}:</span>
                <div className="footer-meta-modal-list">
                  {themes.map((item, index) => {
                    const text = formatItem(item.name, item.count);
                    return (
                      <button
                        key={`theme-${item.name}-${index}`}
                        type="button"
                        className="footer-meta-modal-item"
                        onClick={() => {
                          dispatchClick('theme', text, index);
                          onClose();
                        }}
                      >
                        {text}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {interest && interest.length > 0 && (
              <div className="footer-meta-modal-section">
                <span className="footer-meta-modal-label">{t('cardPosts.interest').replaceAll(':', '')}:</span>
                <div className="footer-meta-modal-list">
                  {interest.map((item, index) => (
                    <button
                      key={`interest-${item.name}-${index}`}
                      type="button"
                      className="footer-meta-modal-item"
                      onClick={() => {
                        dispatchClick('interest', item.name, index);
                        onClose();
                      }}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </dialog>
    </div>
  );
};
